import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FwButtonComponent,
  FwDialogActionsComponent,
  FwDialogComponent,
  FwDialogContentComponent,
  FwMenuItemComponent,
  FwSelectMenuComponent,
  FwTextInputComponent,
  FwWrappedInputComponent,
} from '@flywheel-io/vision';
import { sortBy } from 'lodash';
import { first, map } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { PermissionService } from '../../permission.service';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { ProjectsService } from '../projects.service';
import { ProjectsQueryService } from '../projects-query.service';

interface DialogData {
  groupId?: string;
  isAdmin?: boolean;
}

@Component({
  selector: 'create-project',
  styleUrls: ['./create-project-dialog.component.scss'],
  templateUrl: './create-project-dialog.component.html',
  imports: [
    AsyncPipe,
    FwButtonComponent,
    FwDialogActionsComponent,
    FwDialogComponent,
    FwDialogContentComponent,
    FwMenuItemComponent,
    FwSelectMenuComponent,
    FwTextInputComponent,
    FwWrappedInputComponent,
    ReactiveFormsModule,
  ],
})
export class CreateProjectDialog implements OnInit {
  private data = inject<DialogData>(DIALOG_DATA, { optional: true });
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private projectsService = inject(ProjectsService);
  private projectsQueryService = inject(ProjectsQueryService);
  private permissionService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);
  protected dialogRef = inject(DialogRef);
  protected cdr = inject(ChangeDetectorRef);

  protected projectsLabel = new FormControl('', Validators.required);
  protected groupPicker = new FormControl(null, Validators.required);
  protected groupsWithAccess$ = this.projectsService
    .getGroupsWithAccess(ContainerAccess.Admin, this.data?.isAdmin)
    .pipe(map((groups) => sortBy(groups, (group) => (group.label || group._id).toLowerCase())));

  ngOnInit(): void {
    this.groupPicker.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // manually reset the duplicateLabel error from the API
      if (this.projectsLabel.hasError('duplicateLabel')) {
        this.projectsLabel.setErrors(null);
      }
    });

    this.groupsWithAccess$.pipe(first()).subscribe((groups) => {
      // only assign the group from context if we have permission to add projects to it
      if (this.data && this.data.groupId) {
        const group = groups.find((g) => g._id === this.data.groupId);
        if (group) {
          this.groupPicker.setValue(group._id);
        }
      }
    });
  }

  submit(): void {
    this.coreApi.projects
      .post({ label: this.projectsLabel.value, group: this.groupPicker.value }, { inherit: true })
      .subscribe({
        next: (project) => {
          this.notifications.success('projectAdmin.createNew.success');
          this.projectsService.getProjects({ refresh: true });
          this.projectsQueryService.invalidateProjectListCache();
          this.permissionService.invalidatePermissionsCache();
          this.dialogRef.close(project);
        },
        error: (error: HttpErrorResponse) => {
          this.notifications.error('projectAdmin.createNew.error');
          if (error.status === 409) {
            this.projectsLabel.setErrors({
              duplicateLabel: true,
            });
            this.cdr.markForCheck();
          }
          this.logger.error(error);
        },
      });
  }

  protected generateProjectLabel(): string {
    if (!this.projectsLabel.touched) {
      return '';
    }

    if (this.projectsLabel.hasError('required')) {
      return 'Project label is required';
    } else if (this.projectsLabel.hasError('duplicateLabel')) {
      return 'Project label must be unique in the group';
    }

    return '';
  }
}
